import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import {
    BoxContainer,
    BoxTitle,
    BoxHead,
    Underline,
    FilterTableWrapper,
    FilterWrapper,
} from "../../Components/Styles/PageStyles";
import NotFoundModel from "../../Components/NotFoundModel";
import { useSelector } from "react-redux";
import SearchFilterBox from "../../Components/SearchFilterBox";
import { toast } from "react-toastify";
import { SummarySkeletonLoading } from "../../Components/SkeletonLoading";
import moment from "moment";
import AgreementTable from "../../Components/LandlordPortal/Agreement/AgreementTable";
import TableForExpiring from "../../Components/LandlordPortal/Agreement/TableForExpiring";
import SummaryCardsForExpiring from "../../Components/LandlordPortal/Agreement/SummaryCardForExpiring";
import TableForLockin from "../../Components/LandlordPortal/Agreement/TableForLockin";
import SummaryCardsForLockin from "../../Components/LandlordPortal/Agreement/SummaryCardForLockin";

const LandlordAgreement = () => {
    const searchOptions = [
        { label: "Mobile", value: 1 },
        { label: "Name", value: 2 },
        { label: "Prop Name", value: 3 },
    ];

    const user = useSelector((state) => state.UserReducer.user);
    const [selectedData, setSelectedData] = useState("");
    const [apiData, setApiData] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const [filterType, setFilterType] = useState("");
    const [activeTab, setActiveTab] = useState("1");
    const [searchQuery, setSearchQuery] = useState("");
    const [summaryData, setSummaryData] = useState({
        totalMovedOut: 0,
        curMonthMovedOut: 0,
        defaulters: 0,
        totalDefaultedDues: 0,
    });
    const [masterData, setMasterData] = useState();
    const [filter, setFilter] = useState("");

    const fetchFunction = async (params) => {
        const query = `&s=${params.searchQuery || ""}&t=${params.filterType || ""}&filter=${params.filter || ""}`;
        let url = `${Config.apiUrl}/landlord/list/leases?${query}`;
        const response = await axios.get(
            url,
            {
                headers: {
                    authorization: "Bearer " + user.token,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.isSuccess) {
            if (activeTab === "1") {
                setApiData(res.leases || []);
                setIsFocused(true);
            } else if (activeTab === "2") {
                setApiData(res?.leases || []);
                setSummaryData(res?.summary || {});
                setMasterData(res?.leases || []);
            } else if (activeTab === "3") {
                setApiData(res?.leases || []);
                setSummaryData(res?.summary || {});
                setMasterData(res?.leases || []);
            }
        }
    };

    const getError = (err) => {
        toast.error(err?.response?.data?.msg || "An Error Occurred");
    };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        let filterValue = "";
        if (activeTab === "2") {
            filterValue = "LE";
        } else if (activeTab === "3") {
            filterValue = "LC";
        }
        setFilter(filterValue);

        if (activeTab === "1") {
            getListMutate({
                searchQuery,
                filterType,
                filter: ""
            });
        } else {
            getListMutate({
                searchQuery: "",
                filterType: "",
                filter: filterValue
            });
        }
    }, [activeTab, searchQuery, filterType, getListMutate]);

    const handleFilterChange = (values) => {
        setFilterType(values.filterType);
        setSearchQuery(values.searchQuery);
    };

    const handleClearFilters = () => {
        setFilterType("");
        setSearchQuery("");
    };

    const onSummaryBoxClick = (data) => {
        let filteredData;
        const currentDate = moment().startOf("day");

        if (data === "ECM") {
            const startOfMonth = currentDate.clone().startOf("month").format("YYYY-MM-DD");
            const endOfMonth = currentDate.clone().endOf("month").format("YYYY-MM-DD");
            filteredData = masterData.filter((leaseData) => {
                if (!leaseData.endDate) return false;

                const expiryDate = moment(leaseData.endDate);
                return expiryDate.isBetween(startOfMonth, endOfMonth, null, "[]");
            });
        } else if (data === "ENM") {
            const startOfNextMonth = currentDate
                .clone()
                .add(1, "months")
                .startOf("month");
            const endOfNextMonth = currentDate
                .clone()
                .add(1, "months")
                .endOf("month");

            filteredData = masterData.filter((leaseData) => {
                if (!leaseData.endDate) return false;

                const expiryDate = moment(leaseData.endDate);
                return expiryDate.isBetween(
                    startOfNextMonth,
                    endOfNextMonth,
                    null,
                    "[]"
                );
            });
        } else if (data === "E") {
            filteredData = masterData.filter((leaseData) => {
                if (!leaseData.endDate) return false;

                const expiryDate = moment(leaseData.endDate);
                return expiryDate.isBefore(currentDate);
            });
        } else {
            filteredData = masterData;
        }
        setApiData(filteredData);
    };

    const onSummaryBoxClickForLockin = (data) => {
        let filteredData;
        const currentDate = moment().startOf("day");

        if (data === "LCM") {
            const startOfMonth = currentDate.clone().startOf("month").format("YYYY-MM-DD");
            const endOfMonth = currentDate.clone().endOf("month").format("YYYY-MM-DD");

            filteredData = masterData.filter((leaseData) => {
                if (!leaseData.startDate || leaseData.lockInPeriod === undefined) return false;
                const lockinEndDate = moment(leaseData.startDate).add(leaseData.lockInPeriod, 'months');
                return lockinEndDate.isBetween(startOfMonth, endOfMonth, null, "[]");
            });

        } else if (data === "LNM") {
            const startOfNextMonth = currentDate
                .clone()
                .add(1, "months")
                .startOf("month");
            const endOfNextMonth = currentDate
                .clone()
                .add(1, "months")
                .endOf("month");

            filteredData = masterData.filter((leaseData) => {
                if (!leaseData.startDate || leaseData.lockInPeriod === undefined) return false;
                const lockinEndDate = moment(leaseData.startDate).add(leaseData.lockInPeriod, 'months');
                return lockinEndDate.isBetween(startOfNextMonth, endOfNextMonth, null, "[]");
            });

        } else if (data === "L") {
            filteredData = masterData.filter((leaseData) => {
                if (!leaseData.startDate || leaseData.lockInPeriod === undefined) return false;
                const lockinEndDate = moment(leaseData.startDate).add(leaseData.lockInPeriod, 'months');
                return lockinEndDate.isBefore(currentDate);
            });

        } else {
            filteredData = masterData;
        }
        setApiData(filteredData);
    };

    return (
        <BoxContainer>
            <BoxHead>
                <BoxTitle className="!mb-0 whitespace-nowrap">
                    Property Agreements
                    <Underline />
                </BoxTitle>
            </BoxHead>

            <FloorTabs>
                <FloorTab
                    key="1"
                    onClick={() => setActiveTab('1')}
                    $active={activeTab === "1"}
                >
                    All Agreements
                </FloorTab>
                <FloorTab
                    key="2"
                    onClick={() => setActiveTab('2')}
                    $active={activeTab === "2"}
                >
                    Lease Expiring
                </FloorTab>
                <FloorTab
                    key="3"
                    onClick={() => setActiveTab('3')}
                    $active={activeTab === "3"}
                >
                    Lock-in Completed
                </FloorTab>
            </FloorTabs>

            {activeTab === "1" &&
                <FilterTableWrapper>
                    <FilterWrapper>
                        <div className="flex md:space-x-4 md:flex-row flex-col md:gap-0 gap-4">
                            <SearchFilterBox
                                options={searchOptions}
                                handleFilterChange={handleFilterChange}
                                handleClearFilters={handleClearFilters}
                            />
                        </div>
                    </FilterWrapper>
                    {!error && !isLoading && apiData && (
                        <TableWrapper>
                            <AgreementTable
                                apiData={apiData || []}
                                setSelectedData={setSelectedData}
                                selectedData={selectedData}
                            />
                            {(!apiData || apiData.length === 0) && <NotFoundModel />}
                        </TableWrapper>
                    )}
                </FilterTableWrapper>
            }

            {activeTab === "2" &&
                <>
                    {(isLoading || isRefetching) && !isFocused && (
                        <SummarySkeletonLoading count={3} />
                    )}
                    {isFocused && summaryData && (
                        <SummaryCardsForExpiring summaryData={summaryData} onClick={onSummaryBoxClick} />
                    )}

                    {!error && !isLoading && apiData && (
                        <TableWrapper>
                            <TableForExpiring
                                apiData={apiData || []}
                                setSelectedData={setSelectedData}
                                selectedData={selectedData}
                            />
                            {(error || apiData.length === 0) && !isLoading && <NotFoundModel msg={"All good! No leases are expiring."} />}
                        </TableWrapper>
                    )}
                </>
            }

            {activeTab === "3" &&
                <>
                    {(isLoading || isRefetching) && !isFocused && (
                        <SummarySkeletonLoading count={3} />
                    )}
                    {isFocused && summaryData && (
                        <SummaryCardsForLockin summaryData={summaryData} onClick={onSummaryBoxClickForLockin} />
                    )}

                    {!error && !isLoading && apiData && (
                        <TableWrapper>
                            <TableForLockin
                                apiData={apiData || []}
                                setSelectedData={setSelectedData}
                                selectedData={selectedData}
                            />
                            {(error || apiData.length === 0) && !isLoading && <NotFoundModel msg={"No Data Found."} />}
                        </TableWrapper>
                    )}
                </>
            }

            {error && !isLoading && <NotFoundModel />}
            {(isLoading || isRefetching) && <Loading />}
        </BoxContainer>
    );
};

const FloorTabs = tw.div`flex overflow-x-auto bg-white rounded-md my-4 border-b`;
const FloorTab = tw.button`
  ${(p) =>
        p.$active
            ? " text-primary-color border-b-2 border-b-primary-color bg-cyan-50 "
            : " text-gray-700 "} 
   px-5 py-3  cursor-pointer font-medium transition-all duration-200 whitespace-nowrap text-sm
`;
const TableWrapper = tw.div`mt-4 bg-white rounded-md`;

export default LandlordAgreement;